import { useState } from 'react';
import { useAuth } from '../auth/AuthContext.js';
import { useTasks } from '../tasks/useTasksQuery.js';
import { useCurrentReport } from '../reports/useReportsQuery.js';
import ReportText from '../reports/ReportText.js';
import TaskForm from '../tasks/TaskForm.js';
import { isOverdue } from '../../lib/date.js';

const TIPS = [
  "Small steps compound — one task at a time.",
  "Clear your top priority first — the rest gets easier.",
  "Progress, not perfection.",
  "A tidy list is a clear mind.",
];

function greetingPeriod(hour: number): string {
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

function getWeekBounds(offset: number): { start: Date; end: Date; label: string } {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const diffToMon = (day === 0 ? -6 : 1 - day) + offset * 7;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMon);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  const label =
    offset === 0 ? 'This Week' : offset === -1 ? 'Last Week' : `${fmt(start)} – ${fmt(end)}`;
  return { start, end, label };
}

const PRIORITY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' } as const;
const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' } as const;

export default function Home() {
  const { user } = useAuth();
  const [now] = useState(() => new Date());
  const [tip] = useState(() => TIPS[Math.floor(Math.random() * TIPS.length)]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showForm, setShowForm] = useState(false);

  const { start: weekStart, end: weekEnd, label: weekLabel } = getWeekBounds(weekOffset);
  const { start: reportWeekStart, end: reportWeekEnd } = getWeekBounds(-1);

  const { data: allTasks = [], isLoading } = useTasks();
  const { data: currentReport, isLoading: reportLoading } = useCurrentReport();

  const topLevelTasks = allTasks.filter((t) => !t.parentTaskId);
  const isNewUser = !isLoading && topLevelTasks.length === 0;
  const nowMs = new Date().getTime();

  // Nothing existed before the oldest task was created, so paging Prev past
  // that point would only ever show an empty week — disable it there instead
  // of letting the user click into the void indefinitely.
  const earliestActivity =
    topLevelTasks.length > 0 ? Math.min(...topLevelTasks.map((t) => t.createdAt)) : null;
  const prevWeekEnd = getWeekBounds(weekOffset - 1).end.getTime();
  const prevDisabled = earliestActivity === null || prevWeekEnd < earliestActivity;

  const completedThisWeek = topLevelTasks.filter(
    (t) =>
      t.done &&
      t.completedAt !== null &&
      t.completedAt >= weekStart.getTime() &&
      t.completedAt <= weekEnd.getTime()
  );

  // "Pending"/"Overdue" need to reflect a point in time, not always "right
  // now" — otherwise paging to a past week shows the same numbers no matter
  // how far back you go. For the current week that point is now; for a past
  // week it's that week's own end (was this task still open as of then?).
  const asOf = weekOffset >= 0 ? nowMs : weekEnd.getTime();
  const pendingTasks = topLevelTasks.filter(
    (t) => t.createdAt <= asOf && (t.completedAt === null || t.completedAt > asOf)
  );
  const overdueTasks = pendingTasks.filter((t) => t.dueDate && isOverdue(t.dueDate, new Date(asOf)));

  const totalForRate = completedThisWeek.length + pendingTasks.length;
  const completionRate =
    totalForRate === 0 ? 0 : Math.round((completedThisWeek.length / totalForRate) * 100);

  const byPriority = (['high', 'medium', 'low'] as const).map((p) => {
    const tasks = topLevelTasks.filter((t) => t.priority === p);
    const done = tasks.filter((t) => t.done).length;
    return { priority: p, total: tasks.length, done };
  });

  const period = greetingPeriod(now.getHours());
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="home">
      <div className="home__hero">
        <div className="home__shimmer" />
        <span className="home__badge">Welcome back</span>
        <h2 className="home__greeting">
          {period}
          {user?.username ? `, ${user.username}` : ''}
        </h2>
        <p className="home__subtitle">Let's keep the momentum going.</p>
        <div className="home__tip-rail">
          <span className="home__tip-date">{dateStr}</span>
          <span className="home__tip-rule" />
          <span className="home__tip-text">{tip}</span>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading...</div>
      ) : isNewUser ? (
        <div className="onboard-card">
          <div className="onboard-card__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <h3 className="onboard-card__title">Let's add your first task</h3>
          <p className="onboard-card__body">
            Your stats, priority breakdown, completed tasks, and weekly AI check-in will all show
            up here once you've got some activity — right now there's nothing to measure yet.
          </p>
          <div className="onboard-card__actions">
            <button className="btn btn--primary" onClick={() => setShowForm(true)}>
              + Create your first task
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="dashboard__nav">
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setWeekOffset((o) => o - 1)}
              disabled={prevDisabled}
            >
              ← Prev
            </button>
            <span className="dashboard__week-label">{weekLabel}</span>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setWeekOffset((o) => o + 1)}
              disabled={weekOffset >= 0}
            >
              Next →
            </button>
          </div>

          <div className="dashboard__date-range">
            {weekStart.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}{' '}
            –{' '}
            {weekEnd.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>

          <div className="dashboard__stats">
            <div className="dashboard__stat-card">
              <span className="dashboard__stat-value dashboard__stat-value--success">
                {completedThisWeek.length}
              </span>
              <span className="dashboard__stat-label">Completed this week</span>
            </div>
            <div className="dashboard__stat-card">
              <span className="dashboard__stat-value">{pendingTasks.length}</span>
              <span className="dashboard__stat-label">Pending</span>
            </div>
            <div className="dashboard__stat-card">
              <span className="dashboard__stat-value dashboard__stat-value--danger">
                {overdueTasks.length}
              </span>
              <span className="dashboard__stat-label">Overdue</span>
            </div>
            <div className="dashboard__stat-card">
              <span className="dashboard__stat-value">{completionRate}%</span>
              <span className="dashboard__stat-label">Completion rate</span>
            </div>
          </div>

          <div className="dashboard__section">
            <h3 className="dashboard__section-title">Priority Breakdown</h3>
            <div className="dashboard__priority-rows">
              {byPriority.map(({ priority, total, done }) => (
                <div key={priority} className="dashboard__priority-row">
                  <span
                    className="dashboard__priority-label"
                    style={{ color: PRIORITY_COLORS[priority] }}
                  >
                    {PRIORITY_LABELS[priority]}
                  </span>
                  <div className="dashboard__progress-track">
                    <div
                      className="dashboard__progress-fill"
                      style={{
                        width: total === 0 ? '0%' : `${Math.round((done / total) * 100)}%`,
                        background: PRIORITY_COLORS[priority],
                      }}
                    />
                  </div>
                  <span className="dashboard__priority-count">{done}/{total}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard__section">
            <h3 className="dashboard__section-title">Completed This Week</h3>
            {completedThisWeek.length === 0 ? (
              <p className="dashboard__empty">No tasks completed this week yet. Keep going!</p>
            ) : (
              <div className="dashboard__completed-list">
                {[...completedThisWeek]
                  .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
                  .map((task) => (
                    <div key={task.taskId} className="dashboard__completed-item">
                      <span className="dashboard__completed-check">✓</span>
                      <span className="dashboard__completed-title">{task.title}</span>
                      <span className="dashboard__completed-time">
                        {task.completedAt
                          ? new Date(task.completedAt).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })
                          : ''}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {(weekOffset === 0 || weekOffset === -1) && (
            <div className="dashboard__section">
              <h3 className="dashboard__section-title">
                {`Your AI Report for ${fmt(reportWeekStart)} – ${fmt(reportWeekEnd)}`}
              </h3>

              {reportLoading && <div className="loading">Loading...</div>}

              {!reportLoading && currentReport?.status === 'none' && (
                <p className="dashboard__report-hint">
                  Your first weekly report arrives Sunday night. Keep adding and completing tasks!
                </p>
              )}

              {(currentReport?.status === 'pending' || currentReport?.status === 'processing') && (
                <div className="report-view__status">
                  <div className="spinner" />
                  <span>Preparing your weekly report...</span>
                </div>
              )}

              {currentReport?.status === 'failed' && (
                <p className="dashboard__report-hint">
                  We couldn't generate your report this week. It'll try again next Friday.
                </p>
              )}

              {currentReport?.status === 'ready' && currentReport.reportText && (
                <ReportText text={currentReport.reportText} />
              )}
            </div>
          )}
        </>
      )}

      {showForm && <TaskForm onClose={() => setShowForm(false)} />}
    </div>
  );
}
